import { motion } from "framer-motion";
import { Award, Languages } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SKILL_CATEGORIES, CERTIFICATIONS, LANGUAGES } from "@/content";

const SkillBar = ({ name, level }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between">
      <span className="text-sm font-medium">{name}</span>
      <span className="font-mono text-[10px] text-muted-foreground">{level}%</span>
    </div>
    <div className="h-1.5 overflow-hidden rounded-full bg-border/60">
      <motion.div
        className="h-full rounded-full bg-terra"
        initial={{ width: 0 }}
        whileInView={{ width: `${level}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      />
    </div>
  </div>
);

export const Skills = () => (
  <section
    id="skills"
    data-testid="skills-section"
    className="scroll-mt-24 bg-secondary/40 px-6 py-24 sm:px-12 lg:px-20 lg:py-32"
  >
    <div className="mx-auto max-w-7xl">
      <SectionHead label="03 — Skills" title="The toolkit behind the growth curves." testId="skills-heading" />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-6 lg:grid-cols-12" data-testid="bento-skills-grid">
        {SKILL_CATEGORIES.map((cat, i) => (
          <Reveal key={cat.title} delay={i * 0.07} className={`md:col-span-3 ${cat.span}`}>
            <div className="h-full rounded-[1.75rem] border border-border/60 bg-card/70 p-7 transition-colors duration-300 hover:border-terra/50">
              <p className="mono-label mb-5">{cat.title}</p>
              <div className="space-y-4">
                {cat.skills.map((s) => (
                  <SkillBar key={s.name} {...s} />
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {cat.chips.map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-border/70 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.15} className="md:col-span-6 lg:col-span-7">
          <div className="flex h-full flex-col justify-between gap-6 rounded-[1.75rem] border border-border/60 bg-card/70 p-7 sm:flex-row sm:items-center">
            <div>
              <p className="mono-label mb-4 flex items-center gap-2">
                <Award size={13} />
                Certifications
              </p>
              <ul className="space-y-2">
                {CERTIFICATIONS.map((c) => (
                  <li key={c} className="font-medium">{c}</li>
                ))}
              </ul>
            </div>
            <div className="sm:text-right">
              <p className="mono-label mb-4 flex items-center gap-2 sm:justify-end">
                <Languages size={13} />
                Languages
              </p>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {LANGUAGES.map((l) => (
                  <span key={l} className="rounded-full bg-terra/10 px-4 py-1.5 text-sm text-terra">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2} className="md:col-span-6 lg:col-span-5">
          <div className="flex h-full flex-col justify-center rounded-[1.75rem] border border-terra/30 bg-terra/10 p-7">
            <p className="font-serif text-4xl tracking-tight text-terra">12-member</p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              cross-functional team led at Creafinity — with 5+ interns mentored
              into confident marketers.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
